import { useMemo } from "react";
import Card from "./Card";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell
} from "recharts";

// High risk exposure = red; low = green
const getBarColor = (score) => {
    if (score >= 75) return "#dc2626"; // red    = critical
    if (score >= 50) return "#f97316"; // orange = high
    if (score >= 25) return "#facc15"; // yellow = medium
    return "#22c55e";                  // green  = low
};

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const { framework, score } = payload[0].payload;
        const risk = score >= 75 ? "Critical" : score >= 50 ? "High" : score >= 25 ? "Medium" : "Low";
        return (
            <div className="bg-white border p-2 text-sm shadow-lg rounded">
                <div className="font-semibold">{framework}</div>
                <div>Risk Exposure: {score}%</div>
                <div>Risk Level: <span style={{ color: getBarColor(score) }}>{risk}</span></div>
            </div>
        );
    }
    return null;
};

// frameworkScores: array of {framework, score} from the framework-summary API endpoint
const FrameworkComplianceBars = ({ frameworkScores }) => {
    const chartData = useMemo(() => {
        if (!Array.isArray(frameworkScores) || !frameworkScores.length) return [];
        return frameworkScores;
    }, [frameworkScores]);

    if (!chartData.length) return null;

    return (
        <Card>
            <h3 className="text-lg font-semibold mb-4">Framework Risk Exposure</h3>
            <div style={{ width: "100%", height: 250 }}>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                        data={chartData}
                        margin={{ top: 5, right: 25, left: 5, bottom: 5 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            dataKey="framework"
                            type="category"
                            tick={{ fontSize: 13 }}
                            angle={-35}
                            textAnchor="end"
                            interval={0}
                            height={120}
                        />
                        <YAxis type="number" domain={[0, 100]} unit="%" />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="score" barSize={50} minPointSize={5}>
                            {chartData.map((entry) => (
                                <Cell key={entry.framework} fill={getBarColor(entry.score)} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
            <div className="mt-4 flex flex-wrap gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block bg-red-600" /> ≥75% Critical</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block bg-orange-500" /> 50–74% High</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block bg-yellow-400" /> 25–49% Medium</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block bg-green-500" /> &lt;25% Low</span>
            </div>
        </Card>
    );
};

export default FrameworkComplianceBars;
